import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ApprovalStatus, ApprovalStatusDescriptions } from '../../models/enums/approvalStatus';
import { UtilityService } from '../../services/utility/utility.service';
import { SearchByNameResponse } from '../../models/account/searchByNameResponse';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { MindlerTestRequestResponseSuperAdmin } from '../../models/super-admin/mindlerTestRequestResponseSuperAdmin';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { AssessmentType } from '../../models/enums/enums';
import { AssessmentTypeDescriptions } from '../../models/enums/assessmentType';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-mindler-test',
  standalone: true,
  imports: [
    AutoCompleteModule, DropdownModule, CalendarModule, FormsModule, 
    ReactiveFormsModule, CommonModule, ButtonModule, TableModule, ToastModule],
  templateUrl: './mindler-test.component.html',
  styleUrl: './mindler-test.component.scss'
})
export class MindlerTestComponent implements OnInit {
  submitted: boolean = false;
  filterForm!: FormGroup;
  names:SearchByNameResponse[] = [];
  students: MindlerTestRequestResponseSuperAdmin[] = [];
  statuses = [
    { name: ApprovalStatusDescriptions[ApprovalStatus.Pending], value: 0 },
    { name: ApprovalStatusDescriptions[ApprovalStatus.Approved], value: 1 },
    { name: ApprovalStatusDescriptions[ApprovalStatus.Rejected], value: 2 }
  ];

  isLoading: boolean = false;
  isSearching: boolean = false;
  total: number = 0;
  first: number = 0;
  rowSize: number = 10;

  isAccepting: boolean = false;
  isRejecting: boolean = false;
  isExportLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private superAdminService: SuperAdminService,
    private messageService: MessageService,
    private router: Router
  ){}

  ngOnInit() {
    this.filterForm = this.fb.group({
      name:[null],        
      fromDate: [null],
      toDate: [null],
      status: [null]
    });
    this.search(1);
  }

  viewProfile(id: string): void{
    this.router.navigate(['/admin','view-profile', id]);
  }

  getAssessmentTypeDescription(assessmentType: AssessmentType){
    return AssessmentTypeDescriptions[assessmentType];
  }

  filterNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.names = response.result
        }
      });
    } else {
      this.names = [];
    }
  }

  search(pageNumber: Number){
    this.isSearching = true;
    this.superAdminService.getAllMindlerTestRequest({
      status: this.filterForm.value.status ?? null,
      pageNumber: pageNumber,
      pageSize: this.rowSize,
      fromDate: this.filterForm.value.fromDate ?? null,
      toDate: this.filterForm.value.toDate ?? null,
      searchText: this.filterForm.value.name ?? null
    }).subscribe({
      next: (response) => {
        this.students = response.result;
        this.total = response.total;
        this.isSearching = false;
      }
    })
  }
  acceptTestRequest(studentid: string, index: number){
    this.isAccepting = true;
    this.superAdminService.updateMindlerTestRequestStatus({
      studentId: studentid,
      status: ApprovalStatus.Approved
    }).subscribe({
      next: (response) => {
        this.isAccepting = false;
        this.students[index].status = ApprovalStatus.Approved;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Mindler test request accepted'});
      },
      error: (err) => {
        this.isAccepting = false;
        this.messageService.add({severity: 'error', summary: "Error", detail: 'Failed to accept mindler test request'});
      }
    })
  }
  rejectTestRequest(studentid: string, index: number){
    this.isRejecting = true;
    this.superAdminService.updateMindlerTestRequestStatus({
      studentId: studentid,
      status: ApprovalStatus.Rejected
    }).subscribe({
      next: (response) => {
        this.isRejecting = false;
        this.students[index].status = ApprovalStatus.Rejected;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Mindler test request rejected'});
      },
      error: (err) => {
        this.isRejecting = false;
        this.messageService.add({severity: 'error', summary: "Error", detail: 'Failed to reject mindler test request'});
      }
    })
  }
  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.search(pageNumber);
  }

  applyFilters(): void {
    this.first = 0;
    this.search(1);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.first = 0;
    this.search(1);
  }

  downloadMindlerTestList(): void {
    this.isExportLoading = true;
    this.superAdminService.downloadMindlerTestList({
      Status: this.filterForm.value.status ?? null,
      PageNumber: 1,
      PageSize: 2147483647,
      FromDate: this.filterForm.value.fromDate ?? null,
      ToDate: this.filterForm.value.toDate ?? null,
      SearchText: this.filterForm.value.name ?? null
    }).subscribe({
      next: (resp) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd)
          || `Mindler_Test_Requests_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
        const blob = new Blob([resp.body!], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        this.saveBlobAsFile(blob, filename);
        this.isExportLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File downloaded successfully' });
      },
      error: (err) => {
        this.isExportLoading = false;
        console.error('Download failed', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download file' });
      }
    });
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches.length > 1) {
        return matches[1].replace(/['"]/g, '');
      }
    }
    return null;
  }

  private saveBlobAsFile(blob: Blob | null, filename: string): void {
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }

}
